import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Clock,
  MapPin,
  Users,
  Calendar,
  Share2,
  BookmarkCheck,
  Bookmark,
} from "lucide-react";
import { format } from "date-fns";
import { sessionsAPI, scheduleAPI } from "../services/api";
import { useApp } from "../contexts/AppContext";
import "./SessionDetail.css";

const SessionDetail = () => {
  const { id, eventId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useApp();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);

  useEffect(() => {
    fetchSession();
  }, [id]);

  const fetchSession = async () => {
    setLoading(true);
    try {
      const response = await sessionsAPI.getById(id);
      setSession(response.data);
      setIsBookmarked(response.data.is_bookmarked);
    } catch (error) {
      console.error("Failed to fetch session:", error);
      setSession({
        id,
        title: "Opening Keynote: Future of Education",
        description:
          "Join us for an inspiring opening session about the transformation of education in the digital age. This keynote will explore how technology, innovation, and human-centered design are reshaping learning experiences globally.",
        type: "keynote",
        start_time: "2025-01-20T09:00:00",
        end_time: "2025-01-20T10:30:00",
        location: "Main Auditorium",
        capacity: 500,
        registered: 350,
        speakers: [
          {
            id: "1",
            name: "Dr. Anita Sharma",
            title: "CEO, Education Innovation",
            bio: "Dr. Sharma has over 20 years of experience in education reform.",
          },
        ],
        tags: ["Education", "Innovation", "Technology"],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBookmark = async () => {
    try {
      if (isBookmarked) {
        await scheduleAPI.removeFromSchedule(id);
        setIsBookmarked(false);
        showToast("Removed from schedule", "success");
      } else {
        await scheduleAPI.addToSchedule(id);
        setIsBookmarked(true);
        showToast("Added to schedule", "success");
      }
    } catch (error) {
      showToast("Failed to update", "error");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: session.title,
          text: `Check out this session at RF Conference 2025`,
          url: window.location.href,
        });
      } catch (error) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      showToast("Link copied to clipboard", "success");
    }
  };

  const formatTime = (dateString) => {
    try {
      return format(new Date(dateString), "h:mm a");
    } catch {
      return dateString;
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "EEEE, MMMM d");
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="session-detail-page">
        <div className="session-detail-skeleton">
          <div
            className="skeleton"
            style={{ height: 28, width: 100, marginBottom: 16 }}
          />
          <div
            className="skeleton"
            style={{ height: 32, width: "100%", marginBottom: 8 }}
          />
          <div
            className="skeleton"
            style={{ height: 32, width: "80%", marginBottom: 24 }}
          />
          <div
            className="skeleton"
            style={{ height: 20, width: "60%", marginBottom: 16 }}
          />
          <div className="skeleton" style={{ height: 100, width: "100%" }} />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="session-detail-page">
        <div className="empty-state">
          <h3>Session not found</h3>
          <p>This session may have been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="session-detail-page">
      <div className="session-detail-header">
        <span className="session-type-badge">{session.session_type}</span>
        <h1 className="session-title">{session.title}</h1>
      </div>

      <div className="session-info-cards">
        <div className="info-card">
          <Calendar size={20} />
          <div>
            <span className="info-label">Date</span>
            <span className="info-value">{formatDate(session.start_time)}</span>
          </div>
        </div>
        <div className="info-card">
          <Clock size={20} />
          <div>
            <span className="info-label">Time</span>
            <span className="info-value">
              {formatTime(session.start_time)} - {formatTime(session.end_time)}
            </span>
          </div>
        </div>
        <div className="info-card">
          <MapPin size={20} />
          <div>
            <span className="info-label">Location</span>
            <span className="info-value">{session.location}</span>
          </div>
        </div>
        {session.capacity && (
          <div className="info-card">
            <Users size={20} />
            <div>
              <span className="info-label">Capacity</span>
              <span className="info-value">
                {session.registered || 0}/{session.capacity} registered
              </span>
            </div>
          </div>
        )}
      </div>

      <section className="session-section">
        <h2>About this Session</h2>
        <p className="session-description">{session.description}</p>
      </section>

      {session.speakers && session.speakers.length > 0 && (
        <section className="session-section">
          <h2>Speakers</h2>
          <div className="session-speakers">
            {session.speakers.map((speaker) => (
              <div
                key={speaker.id}
                className="speaker-card-mini"
                onClick={() =>
                  navigate(
                    eventId
                      ? `/event/${eventId}/speaker/${speaker.id}`
                      : `/speaker/${speaker.id}`,
                  )
                }
              >
                <div className="speaker-avatar">
                  {speaker.photo ? (
                    <img src={speaker.photo} alt={speaker.name} />
                  ) : (
                    speaker.name?.charAt(0)
                  )}
                </div>
                <div className="speaker-info">
                  <h4>{speaker.name}</h4>
                  <p>{speaker.title}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {session.tags && session.tags.length > 0 && (
        <section className="session-section">
          <h2>Topics</h2>
          <div className="tag-list">
            {session.tags.map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        </section>
      )}

      <div className="session-actions">
        <button
          className={`btn ${isBookmarked ? "btn-accent" : "btn-primary"} btn-lg`}
          onClick={handleToggleBookmark}
        >
          {isBookmarked ? <BookmarkCheck size={20} /> : <Bookmark size={20} />}
          {isBookmarked ? "Added to Schedule" : "Add to My Schedule"}
        </button>
        <button className="btn btn-outline btn-lg" onClick={handleShare}>
          <Share2 size={20} />
          Share
        </button>
      </div>
    </div>
  );
};

export default SessionDetail;
